import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { RADIUS } from "../../../theme/tokens";
import type { PbjPlanActionProgress, PbjPlanValue } from "../../../utils/models";
import { type ActionFormat, formatPlanValueInDirection } from "./planValue.utils";

type Props = {
	action: Pick<PbjPlanActionProgress, "id" | "direction" | "goalRate" | "goalRateWeekly" | "updatedGoal">;
	format: ActionFormat;
	/** Whether the lever reads behind — only then is the raised catch-up rate shown beside the committed one. */
	behind: boolean;
};

/** An action's committed rate, and the raised rate needed to recover when it is behind. */
export const PlanActionRateBox: FC<Props> = ({ action, format, behind }) => {
	const { t } = useTranslation();
	const isCut = action.direction === "cut";
	const perWeekSuffix = t("pbj_model.tracker.per_week_suffix");
	// The committed rate is quoted per WEEK so it is comparable to `updatedGoal`, which sits beside it
	// behind an arrow and is per week by construction.
	//
	// DEFAULT: falls back to the per-day rate when the weekly twin is absent. Flagged per repo standards.
	// The weekly figure is optional by contract — an older backend does not ship it, and formatting
	// `undefined` would blank the tab. The suffix follows the value rather than being pinned to "week",
	// so the fallback is labelled for what it actually is instead of passing a per-day figure off as weekly.
	const committedRate = action.goalRateWeekly ?? action.goalRate;
	const committedRateSuffix = action.goalRateWeekly ? perWeekSuffix : t("pbj_model.tracker.per_day_suffix");
	const inDirection = (value: PbjPlanValue): string => `${formatPlanValueInDirection(value, format.basis, action.direction)} ${format.unit}`;

	return (
		<Stack
			direction="row"
			spacing={2}
			sx={(theme) => ({
				alignItems: "center",
				flexShrink: 0,
				px: 2,
				py: 1,
				borderRadius: `${RADIUS.CONTROL}px`,
				border: `1px solid ${theme.palette.divider}`,
				bgcolor: alpha(theme.palette.text.primary, 0.02),
			})}
		>
			<Box>
				<Typography variant="caption" sx={{ display: "block", fontWeight: 600, color: "text.secondary" }}>
					{t(isCut ? "pbj_model.tracker.goal_rate_cut" : "pbj_model.tracker.goal_rate")}
				</Typography>
				<Typography variant="subtitle2" sx={{ fontWeight: 700 }} data-testid={`pbj-model-plan-action-goal-${action.id}`}>
					{`${inDirection(committedRate)}${committedRateSuffix}`}
				</Typography>
			</Box>
			{action.updatedGoal !== null && behind && (
				<>
					<ArrowForwardIcon sx={{ fontSize: 16, color: "text.disabled" }} />
					<Box>
						<Typography variant="caption" sx={{ display: "block", fontWeight: 600, color: "error.main" }}>
							{t("pbj_model.tracker.updated_goal")}
						</Typography>
						<Typography variant="subtitle2" sx={{ fontWeight: 700, color: "error.main" }} data-testid={`pbj-model-plan-action-updated-goal-${action.id}`}>
							{`${inDirection(action.updatedGoal)}${perWeekSuffix}`}
						</Typography>
					</Box>
				</>
			)}
		</Stack>
	);
};
